import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { FeatureCard } from "@/components/marketing/FeatureCard";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  FileText,
  Package,
  CreditCard,
  FileCheck,
  Award,
  BarChart3,
  Search,
  Star,
  GitCompare,
  ListChecks,
  Printer,
  Gift,
  PanelTop,
  Shirt,
  CheckCircle2,
  XCircle,
} from "lucide-react";

export default function SupplierDiversity() {
  const comparisonData = [
    { feature: "Tier 1 Eligible", diy: "Yes", broker: "Typically No (they become vendor of record)", radius: "Yes", diyCheck: true, brokerCheck: false, radiusCheck: true },
    { feature: "Vendor Verification", diy: "You do it yourself", broker: "They choose vendors for you", radius: "Pre-verified, you choose" },
    { feature: "Competitive Pricing", diy: "Requires your own RFQ process", broker: "Opaque markups", radius: "Transparent competitive bidding" },
    { feature: "Procurement Controls", diy: "Manual processes", broker: "Limited visibility", radius: "Built-in approval workflows" },
    { feature: "Audit Trail", diy: "You build it", broker: "Depends on provider", radius: "Automatic documentation" },
    { feature: "Diversity Certification Tracking", diy: "Manual verification", broker: "Their responsibility", radius: "Verified and maintained" },
    { feature: "Relationship Ownership", diy: "You own it", broker: "They own it", radius: "You own it" },
    { feature: "Time Investment", diy: "High", broker: "Low", radius: "Low" },
    { feature: "Cost", diy: "High (internal resources)", broker: "High (opaque markups and rebates)", radius: "Low (transparent transaction fee)" },
  ];

  const faqItems = [
    {
      question: "How does Radius verify supplier diversity certifications?",
      answer: "During vendor enrollment, we collect diversity certifications from recognized bodies including NMSDC, WBENC, NVBDC, NGLCC, Disability:IN, SBA 8(a), and HUBZone. Certification expiration dates are tracked with automated renewal reminders to vendors."
    },
    {
      question: "Why might purchases through Radius qualify as Tier 1 diversity spend?",
      answer: "Tier 1 classification typically requires a direct contractual relationship between buyer and diverse supplier. Unlike brokers or distributors who become your vendor of record, Radius facilitates direct purchase orders between your organization and the supplier. We act as a payment facilitator, not an intermediary. Your supplier diversity team makes final classification decisions, we provide documentation to support their review."
    },
    {
      question: "What diversity certifications do you recognize?",
      answer: "We track certifications from major certifying bodies: NMSDC (Minority Business Enterprise), WBENC (Women's Business Enterprise), NVBDC (Veteran-Owned), NGLCC (LGBT Business Enterprise), Disability:IN (Disability-Owned), SBA 8(a), HUBZone, and state/local certifications. Vendors can list multiple certifications."
    },
    {
      question: "Can I run reports for my supplier diversity program?",
      answer: "Yes. The platform provides diversity spend reports showing total spend with certified diverse suppliers, broken down by certification type, product category, and time period. Reports can be exported for inclusion in your corporate diversity reporting."
    },
    {
      question: "Do diverse suppliers pay to be on Radius?",
      answer: "No. There are no enrollment fees or subscription costs for suppliers. Radius charges a transparent platform fee only when transactions are completed successfully. This removes barriers for diverse suppliers and ensures you have access to a wide range of certified vendors."
    },
    {
      question: "What if I already have diverse suppliers I work with?",
      answer: "You can invite your existing diverse suppliers to join the Radius network. Once enrolled and verified, they'll appear in your supplier searches and can receive RFQs through the platform. This consolidates your diverse supplier relationships into one system while maintaining those valuable partnerships."
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Supplier Diversity - Tier 1 Eligible Transactions"
        description="Connect directly with certified diverse suppliers for print, packaging, and promotional products. Maintain Tier 1 spend eligibility with our marketplace platform."
        canonical="/supplier-diversity"
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="container relative py-12 md:py-20">
          <div className="mx-auto max-w-4xl text-center space-y-8">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              Meet Your Diversity Spend Goals <span className="text-primary">With Tier 1 Eligible Transactions</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Connect directly with certified MBE, WBE, and veteran-owned suppliers for print, packaging, promotional products, and branded merchandise without sacrificing procurement controls or diversity spend classification.
            </p>
            <Button size="lg" asChild>
              <Link to="/auth/signup">
                Explore Diverse Suppliers <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>

            {/* Simple Relationship Diagram */}
            <div className="pt-8 max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-4 md:gap-8">
                <div className="flex flex-col items-center">
                  <div className="h-16 w-16 md:h-20 md:w-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-2xl md:text-3xl">🏢</span>
                  </div>
                  <span className="text-sm font-medium mt-2">Your Organization</span>
                </div>
                
                <div className="flex flex-col items-center flex-1">
                  <div className="w-full border-t-2 border-dashed border-primary relative">
                    <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs text-primary font-medium whitespace-nowrap">
                      Direct Contract
                    </span>
                  </div>
                  <div className="mt-4 px-3 py-1.5 rounded-full bg-muted text-xs text-muted-foreground">
                    Radius Facilitates
                  </div>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="h-16 w-16 md:h-20 md:w-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-2xl md:text-3xl">🏭</span>
                  </div>
                  <span className="text-sm font-medium mt-2">Diverse Vendor</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tier 1 Advantage Section */}
      <section className="bg-muted/50 py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-6">
              Preserve Your Tier 1 Diversity Spend Classification
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-4 max-w-3xl mx-auto">
              Many enterprises struggle with a frustrating tradeoff: work directly with diverse suppliers (Tier 1) but juggle scattered vendors, emails, and spreadsheets, or pay a premium to intermediaries for convenience but have spend classified as Tier 2.
            </p>
            <p className="text-lg font-semibold text-center mb-8 max-w-3xl mx-auto">
              Radius eliminates this tradeoff.
            </p>

            {/* Callout Box */}
            <div className="bg-primary text-primary-foreground rounded-lg p-6 mb-12 text-center">
              <p className="text-lg font-semibold">
                Radius operates differently. As a marketplace platform, you contract directly with the diverse supplier, and they fulfill your order. We facilitate the transaction without taking title to the goods.
              </p>
            </div>

            {/* Explanation Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-lg border bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">Direct Contracts</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your purchase order goes to the diverse supplier. You maintain the direct buyer-supplier relationship that supports Tier 1 classification.
                </p>
              </div>

              <div className="p-6 rounded-lg border bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">No Title Transfer</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Products ship directly from the diverse supplier to you. Radius never takes ownership of inventory or goods.
                </p>
              </div>

              <div className="p-6 rounded-lg border bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">Payment Facilitation</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  We process payment on behalf of both parties, providing protection and efficiency without changing the fundamental transaction structure.
                </p>
              </div>

              <div className="p-6 rounded-lg border bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <FileCheck className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">Audit-Ready Documentation</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Complete transaction records show the direct relationship between your organization and certified diverse suppliers.
                </p>
              </div>
            </div>

            {/* Disclaimer */}
            <p className="text-xs text-muted-foreground text-center mt-8 max-w-2xl mx-auto">
              Note: Tier 1 vs. Tier 2 classification is determined by your organization's policies and your legal/procurement teams. We provide transaction structures that may support Tier 1 classification, but recommend confirming with your internal stakeholders.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="container py-20">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">
            Three Ways to Source: Only One Delivers Control, Classification, AND Lower Cost
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Compare your options for working with diverse suppliers
          </p>

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse table-fixed">
              <thead>
                <tr className="bg-muted">
                  <th className="w-1/4 text-left p-4 border font-semibold">Feature</th>
                  <th className="w-1/4 text-center p-4 border font-semibold">Direct Procurement (DIY)</th>
                  <th className="w-1/4 text-center p-4 border font-semibold">Brokers / Print Management</th>
                  <th className="w-1/4 text-center p-4 border font-semibold bg-primary/5">Radius Marketplace</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={row.feature} className={index % 2 === 0 ? "bg-background" : "bg-muted/30"}>
                    <td className="p-4 border font-medium">{row.feature}</td>
                    <td className="p-4 border text-center">
                      {'diyCheck' in row ? (
                        <div className="flex flex-col items-center gap-1">
                          {row.diyCheck ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-destructive" />
                          )}
                          <span className="text-sm text-muted-foreground">{row.diy}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">{row.diy}</span>
                      )}
                    </td>
                    <td className="p-4 border text-center">
                      {'brokerCheck' in row ? (
                        <div className="flex flex-col items-center gap-1">
                          {row.brokerCheck ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-destructive" />
                          )}
                          <span className="text-sm text-muted-foreground">{row.broker}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">{row.broker}</span>
                      )}
                    </td>
                    <td className="p-4 border text-center bg-primary/5">
                      {'radiusCheck' in row ? (
                        <div className="flex flex-col items-center gap-1">
                          {row.radiusCheck ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-destructive" />
                          )}
                          <span className="text-sm font-medium">{row.radius}</span>
                        </div>
                      ) : (
                        <span className="text-sm font-medium">{row.radius}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Approach Explanations */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-lg border bg-card">
              <h3 className="text-lg font-semibold mb-3">The DIY Challenge</h3>
              <p className="text-sm text-muted-foreground">
                Managing direct relationships with diverse suppliers preserves Tier 1 status but requires significant time investment in vendor discovery, vetting, quote management, and ongoing administration.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-card">
              <h3 className="text-lg font-semibold mb-3">The Broker Tradeoff</h3>
              <p className="text-sm text-muted-foreground">
                Brokers and print management companies reduce your workload but typically resell products—making your spend Tier 2 even when they use diverse subcontractors. Plus, you often pay a markup.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-primary/5">
              <h3 className="text-lg font-semibold mb-3">The Radius Approach</h3>
              <p className="text-sm text-muted-foreground">
                Get the efficiency of a managed platform while maintaining direct supplier relationships. Verified vendors, competitive bidding, and complete documentation without sacrificing Tier 1 eligibility.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Diverse Supplier Categories */}
      <section className="bg-muted/50 py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-4">
            Certified Diverse Suppliers Across Your Marketing Supply Chain
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
            Our vendor network includes businesses certified by NMSDC, WBENC, NVBDC, NGLCC, Disability:IN, SBA 8(a), HUBZone, and other recognized certifying bodies.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <FeatureCard
              icon={Printer}
              title="Commercial Printing"
              description="Brochures, catalogs, direct mail, business cards, stationery, and marketing collateral from certified diverse printers."
            />
            <FeatureCard
              icon={Package}
              title="Packaging"
              description="Custom boxes, labels, flexible packaging, and retail packaging solutions from diverse-owned manufacturers."
            />
            <FeatureCard
              icon={Gift}
              title="Promotional Products"
              description="Branded merchandise, corporate gifts, trade show items, and employee recognition products."
            />
            <FeatureCard
              icon={PanelTop}
              title="Signage and Displays"
              description="Trade show displays, retail signage, banners, vehicle wraps, and environmental graphics."
            />
            <FeatureCard
              icon={Shirt}
              title="Branded Apparel"
              description="Corporate uniforms, event merchandise, team apparel, and promotional clothing."
            />
          </div>
        </div>
      </section>

      {/* Platform Features for Diversity Programs */}
      <section className="container py-20">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl font-bold">Built-In Tools for Supplier Diversity Success</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Purpose-built features to support your supplier diversity program
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <FeatureCard
            icon={Award}
            title="Certification Tracking"
            description="Vendor diversity certifications are verified during enrollment and tracked for expiration. Filter searches by certification type."
          />
          <FeatureCard
            icon={BarChart3}
            title="Diversity Spend Reporting"
            description="Track spend with certified diverse suppliers by certification type, category, and time period. Export reports for corporate diversity reporting."
          />
          <FeatureCard
            icon={Search}
            title="Supplier Discovery"
            description="Search and filter vendors by diversity certification, capabilities, and location. Easily identify certified suppliers for your needs."
          />
          <FeatureCard
            icon={Star}
            title="Standardized Quality Reviews"
            description="Platform-wide ratings and reviews help you identify high-performing diverse suppliers based on actual transaction outcomes."
          />
          <FeatureCard
            icon={GitCompare}
            title="RFQ and Competitive Bidding"
            description="Get competitive quotes from multiple certified diverse suppliers. Compare pricing, lead times, and capabilities in one view."
          />
          <FeatureCard
            icon={ListChecks}
            title="Approval Workflows"
            description="Route awards through your procurement approval process. Maintain governance while enabling teams to access diverse suppliers."
          />
          <FeatureCard
            icon={FileCheck}
            title="Audit-Ready Documentation"
            description="Complete transaction records including certifications, quotes, purchase orders, and delivery confirmation for diversity program audits."
          />
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-muted/50 py-20">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">
            From Search to Settlement: Direct Relationships, Complete Support
          </h2>
          <div className="max-w-3xl mx-auto space-y-8">
            {[
              {
                step: "1",
                title: "Discover",
                description: "Search our verified vendor network filtered by diversity certification. View certifications, capabilities, ratings, and past project examples."
              },
              {
                step: "2",
                title: "Request",
                description: "Submit an RFQ to certified diverse suppliers. Our AI-guided specification process ensures clear, complete requirements."
              },
              {
                step: "3",
                title: "Select",
                description: "Compare competitive bids from multiple diverse suppliers. See pricing, lead times, and vendor qualifications side-by-side."
              },
              {
                step: "4",
                title: "Contract",
                description: "Award the job and generate a purchase order directly between your organization and the diverse supplier."
              },
              {
                step: "5",
                title: "Collaborate",
                description: "Communicate with your supplier through the platform. Share files, approve proofs, and track milestones."
              },
              {
                step: "6",
                title: "Pay",
                description: "Secure payment processing upon delivery confirmation. Your payment goes to the diverse supplier (minus transparent platform fee)."
              },
              {
                step: "7",
                title: "Report",
                description: "Access diversity spend reports showing transactions with certified suppliers. Export data for your corporate diversity reporting."
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
                  {item.step}
                </div>
                <div className="pt-2 space-y-2">
                  <h3 className="text-xl font-semibold">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Callout */}
          <div className="max-w-3xl mx-auto mt-12 p-6 rounded-lg bg-primary/5 border border-primary/20 text-center">
            <p className="text-muted-foreground">
              <span className="font-semibold text-foreground">Throughout this process, you maintain the direct contractual relationship with the diverse supplier.</span>
              {" "}Radius provides the platform infrastructure, including verification, payment processing, and documentation, without becoming the seller of record.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container py-20">
        <div className="text-center space-y-6">
          <h2 className="text-3xl font-bold">Ready to Strengthen Your Supplier Diversity Program?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Connect with certified diverse suppliers while maintaining the direct relationships your Tier 1 goals require. 
            Our team can walk you through how Radius supports enterprise diversity programs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/contact">
                Schedule a Demo
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/auth/signup">
                Explore Diverse Suppliers
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-muted/50 py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Supplier Diversity FAQs</h2>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
